import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { createCheckoutSession } from '../../api/endpoints/checkoutv2';
import { PRODUCT_TO_PRICE_ID, PRODUCT_NAMES } from '../../lib/constants';
import useCartStore from '../../store/cartStore';
import { useUtmParams } from '../../hooks/useUtmParams';
import { useAnalytics } from '../../hooks/useAnalytics';
import Loader from '../../components/Loader';
import Toast from '../../components/Toast';
import type { ApiError } from '../../api/types';

const CartPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Hooks
  useUtmParams();
  const { trackEvent } = useAnalytics();
  const { setProduct, setCoupon } = useCartStore();

  // Get coupon from URL
  const couponFromUrl = searchParams.get('coupon');

  // Mutation for creating checkout session
  const mutation = useMutation({
    mutationFn: createCheckoutSession,
    onSuccess: (data) => {
      console.log('[CartPage] Checkout session created:', data);

      // Store session ID
      useCartStore.getState().setSessionId(data.id);

      // Track event
      trackEvent({
        event: 'begin_checkout',
        currency: 'USD',
        value: 0, // Value will be determined by Stripe
      });

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Checkout URL not provided');
      }
    },
    onError: (err: ApiError) => {
      console.error('[CartPage] Checkout session creation failed:', err);
      setError(err.message || 'Failed to create checkout session');

      // Track error event
      trackEvent({
        event: 'checkout_error',
        error: err.message,
        product_id: productId || 'unknown',
      });
    },
  });

  useEffect(() => {
    // Validate product ID
    if (!productId) {
      setError('Product ID is required');
      return;
    }

    // Get price ID from product mapping
    const priceId = PRODUCT_TO_PRICE_ID[productId];
    if (!priceId) {
      setError(`Invalid product ID: ${productId}`);
      return;
    }

    console.log('[CartPage] Product ID:', productId);
    console.log('[CartPage] Price ID:', priceId);
    console.log('[CartPage] Coupon:', couponFromUrl);

    // Store product and coupon
    setProduct(productId, priceId);
    if (couponFromUrl) {
      setCoupon(couponFromUrl);
    }

    // Track page view
    trackEvent({
      event: 'view_cart',
      currency: 'USD',
      items: [
        {
          item_id: productId,
          item_name: PRODUCT_NAMES[productId] || productId,
        },
      ],
    });

    // Create checkout session
    const successUrl = `${window.location.origin}/purchase/thanks?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${window.location.origin}/purchase/cart/${productId}`;

    mutation.mutate({
      priceId,
      coupon: couponFromUrl || undefined,
      successUrl,
      cancelUrl,
    });
  }, [productId, couponFromUrl]);

  // Error state
  if (error) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Checkout Error
            </h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go Home
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
        <Toast
          message={error}
          type="error"
          duration={0}
          onClose={() => setError(null)}
        />
      </>
    );
  }

  // Loading state
  return (
    <Loader
      fullScreen
      message="Redirecting to secure checkout..."
      size="large"
    />
  );
};

export default CartPage;
